import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { useAuth } from "../context/AuthContext";

type Alert = {
  id: number;
  severity: string;
  metric: string;
  value: string | null;
  device_id: number | null;
  customer_id: number | null;
  customer_name?: string | null;
  device_name?: string | null;
  created_at: string;
  resolved_at: string | null;
};

type Ticket = {
  id: number;
  number: string | null;
  subject: string;
  status: string;
  priority: string;
  customer_id: number;
  customer_name?: string | null;
  assignee_user_id: number | null;
  updated_at: string;
};

type Device = {
  id: number;
  hostname: string;
  customer_id: number;
  customer_name?: string | null;
  last_seen: string | null;
  os_name: string | null;
};

const SEV_PILL: Record<string, string> = {
  critical: "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700",
  warning: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700",
  info: "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700",
};

const PRI_PILL: Record<string, string> = {
  high: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  urgent: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  normal: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  low: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
};

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, (Date.now() - then) / 1000);
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function MobileDashboard() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  const load = async () => {
    setErr("");
    setLoading(true);
    try {
      const [aRes, tRes, dRes] = await Promise.all([
        api.get("/api/v1/alerts", { params: { resolved: false, limit: 25 } }),
        user
          ? api.get("/api/v1/tickets", {
              params: { assignee_user_id: user.id, limit: 25, status: "open,in_progress" },
            })
          : Promise.resolve({ data: [] }),
        api.get("/api/v1/devices", { params: { limit: 10 } }),
      ]);
      setAlerts(aRes.data || []);
      setTickets(tRes.data?.items || tRes.data || []);
      setDevices(dRes.data?.items || dRes.data || []);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load mobile dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const criticalCount = alerts.filter((a) => a.severity === "critical").length;
  const openTicketCount = tickets.length;

  return (
    <div className="space-y-4 max-w-screen-md mx-auto px-1">
      {!online && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-900 dark:text-amber-200 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
          You're offline. Data shown may be stale.
        </div>
      )}

      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            On-call dashboard
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Hi {user?.username || "tech"} — your at-a-glance view.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="text-xs px-2 py-1 border dark:border-gray-600 dark:text-gray-100 rounded-lg disabled:opacity-50"
        >
          {loading ? "…" : "↻"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Stat
          label="Critical alerts"
          value={criticalCount}
          accent={criticalCount > 0 ? "danger" : "ok"}
        />
        <Stat
          label="Open tickets (you)"
          value={openTicketCount}
          accent={openTicketCount > 5 ? "warn" : "ok"}
        />
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-300 px-3 py-2 rounded-lg text-xs">
          {err}
        </div>
      )}

      <Section
        title="Critical alerts"
        href="/alerts"
        empty="No critical alerts. Nice."
      >
        {alerts
          .filter((a) => a.severity === "critical")
          .slice(0, 5)
          .map((a) => (
            <Link
              key={a.id}
              to={`/alerts?id=${a.id}`}
              className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {a.metric}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {a.device_name || `device ${a.device_id}`} ·{" "}
                    {a.customer_name || "—"}
                  </div>
                </div>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${
                    SEV_PILL[a.severity] || SEV_PILL.info
                  }`}
                >
                  {a.severity}
                </span>
              </div>
              <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">{timeAgo(a.created_at)}</div>
            </Link>
          ))}
      </Section>

      <Section
        title="My tickets"
        href="/tickets"
        empty="Inbox zero. 🎉"
      >
        {tickets.slice(0, 8).map((t) => (
          <Link
            key={t.id}
            to={`/tickets/${t.id}`}
            className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  #{t.number || t.id} · {t.subject}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {t.customer_name || `customer ${t.customer_id}`} · {t.status}
                </div>
              </div>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full ${
                  PRI_PILL[t.priority] || PRI_PILL.normal
                }`}
              >
                {t.priority}
              </span>
            </div>
            <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
              updated {timeAgo(t.updated_at)}
            </div>
          </Link>
        ))}
      </Section>

      <Section
        title="Recent devices"
        href="/devices"
        empty="No devices yet."
      >
        {devices.slice(0, 5).map((d) => (
          <Link
            key={d.id}
            to={`/devices/${d.id}`}
            className="block px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-900"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {d.hostname}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {d.customer_name || `customer ${d.customer_id}`} · {d.os_name || "—"}
                </div>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400">{timeAgo(d.last_seen)}</span>
            </div>
          </Link>
        ))}
      </Section>

      <div className="text-center pt-2">
        <Link to="/" className="text-xs text-brand dark:text-brand-light hover:underline">
          ← Full desktop dashboard
        </Link>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent: "ok" | "warn" | "danger";
}) {
  const color =
    accent === "danger"
      ? "text-red-700 dark:text-red-300"
      : accent === "warn"
        ? "text-amber-700 dark:text-amber-300"
        : "text-gray-900 dark:text-gray-100";
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3">
      <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-3xl font-bold mt-0.5 font-mono ${color}`}>{value}</div>
    </div>
  );
}

function Section({
  title,
  href,
  empty,
  children,
}: {
  title: string;
  href: string;
  empty: string;
  children: React.ReactNode;
}) {
  const arr = Array.isArray(children) ? children : [children];
  const filtered = arr.filter(Boolean);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h2>
        <Link to={href} className="text-xs text-brand dark:text-brand-light hover:underline">
          See all →
        </Link>
      </div>
      {filtered.length === 0 ? (
        <div className="px-3 py-6 text-xs text-center text-gray-500 dark:text-gray-400 italic">{empty}</div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-700">{children}</div>
      )}
    </div>
  );
}
